import React, {
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useState,
} from "react";
import {
  Autocomplete,
  MenuItem,
  Popper,
  PopperProps,
  TextField,
} from "@mui/material";
import { getCodeData } from "../utils/CommonUtils";
import { StyleSettings } from "../../styles/StyleSettings";

// 드롭다운 폭을 항목 텍스트 길이에 맞게 자동 확장 (최소 폭은 입력창 폭 유지)
// Autocomplete가 인라인 style로 width를 anchor 폭으로 고정하므로 minWidth로 바꿔준다.
const AutoWidthPopper = (props: PopperProps) => (
  <Popper
    {...props}
    placement="bottom-start"
    style={{
      ...props.style,
      width: "auto",
      minWidth: props.style?.width,
    }}
  />
);

interface SnComboBoxProps {
  defaultValue: any;
  code?: string;
  sql?: string;
  emptyLine?: boolean;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  onFocus?: (target: string) => void;
  filter?: string;
  js_combo?: any[] | string;
  /** 드롭다운 항목에 "[코드]" 표시 여부 (기본 true) */
  showCode?: boolean;
  /** 지우기(X) 버튼 숨김 여부 (기본값: isRequired) */
  disableClearable?: boolean;
}

const SnComboBox = React.memo(
  ({
    defaultValue,
    code,
    sql,
    emptyLine,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    isFormPanel = false,
    onFocus,
    filter,
    js_combo,
    showCode = true,
    disableClearable,
  }: SnComboBoxProps) => {
    const [options, setOptions] = useState<any[]>([]);
    const [orgData, setOrgData] = useState<any[]>([]);
    const [value, setValue] = useState<string>("");
    const [inputValue, setInputValue] = useState<string>("");
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");
    const [open, setOpen] = React.useState(false);

    const fetchDefaultValue = async () => {
      try {
        const newValue = defaultValue ? defaultValue : "";
        if (value !== newValue) {
          if (newValue.toLowerCase() === "top1") {
            const dataSource = Array.isArray(js_combo)
              ? js_combo
              : typeof js_combo === "string"
                ? JSON.parse(js_combo)
                : [];
            // js_combo가 없으면 코드 조회(fetchData) 완료 후 첫 항목으로 세팅한다.
            if (dataSource.length === 0) return;
            const updatedValue = dataSource[0].code;
            setValue(updatedValue);
            if (onValueChanged) {
              onValueChanged(updatedValue);
            }
          } else {
            setValue(newValue);
            if (onValueChanged) {
              onValueChanged(newValue);
            }
          }
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    const fetchFilter = async () => {
      try {
        let fil = filter ? filter : "";
        if (fil == "" || fil == null || orgData[0] == null) return;
        Object.keys(orgData[0]).forEach((key) => {
          fil = fil.replace(`[${key}]`, `orgData.${key}`);
        });
        setOptions(
          orgData.filter((orgData) =>
            new Function("orgData", `return ${fil}`)(orgData)
          )
        );
      } catch (error: any) {
        console.log(error);
      }
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    useEffect(() => {
      fetchFilter();
    }, [filter]);

    // 코드값 조회
    useEffect(() => {
      const fetchData = async () => {
        try {
          const dataSource = Array.isArray(js_combo)
            ? js_combo
            : typeof js_combo === "string"
              ? JSON.parse(js_combo)
              : [];
          const data =
            dataSource.length > 0
              ? dataSource
              : await getCodeData(code || "", sql || "", emptyLine);
          const list: any[] = Array.isArray(data) ? data : data ? [data] : [];
          setOrgData(list);

          // defaultValue가 top1이고 js_combo가 없는 경우 조회된 첫 항목을 선택한다.
          if (
            dataSource.length === 0 &&
            (defaultValue || "").toLowerCase() === "top1" &&
            list.length > 0
          ) {
            setValue(list[0].code);
            if (onValueChanged) {
              onValueChanged(list[0].code);
            }
          }

          let fil = filter ? filter : "";
          if (fil == "" || fil == null) {
            setOptions(Array.isArray(data) ? data : data ? [data] : []);
          } else {
            Object.keys(Array.isArray(data) ? data[0] : []).forEach((key) => {
              fil = fil.replace(`[${key}]`, `orgData.${key}`);
            });
            setOptions(
              data.filter((d: any) =>
                new Function("orgData", `return ${fil}`)(d)
              )
            );
          }
        } catch (error) {}
      };
      fetchData();
    }, [code, sql, emptyLine, js_combo]);

    const handleChange = (e: SyntheticEvent, newValue: any) => {
      const updatedValue = newValue?.code || "";
      setValue(updatedValue);
      if (onValueChanged) {
        onValueChanged(updatedValue);
      }

      setTimeout(() => {
        if (!isSearchPanel) {
          if (updatedValue) {
            onFocus && onFocus("next");
          }
        } else {
          onFocus && onFocus("current");
        }
      }, 0);
    };

    const handleKeyDown = async (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          const found = options.find((o) => o.code === inputValue);
          console.log(options, found, inputValue);
          if (found) {
            handleChange({} as React.SyntheticEvent, found);
          }
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      } else if (e.key === "Space" || e.keyCode === 32) {
        if (!value) {
          e.preventDefault();
        }
        setOpen(true);
      }
    };

    return (
      <div>
        <Autocomplete
          disablePortal={!isSearchPanel && !isFormPanel}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disableClearable={disableClearable ?? isRequired}
          options={options}
          value={options.find((option: any) => option.code === value) || null}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          getOptionLabel={(option: any) => option.name}
          isOptionEqualToValue={(option: any, value: any) =>
            option.code === value?.code
          }
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: StyleSettings.font,
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem
              {...props}
              key={option.code}
              sx={{
                fontSize: StyleSettings.font,
                minHeight: "32px",
              }}
            >
              {showCode && option.code
                ? `${option.name} [${option.code}]`
                : option.name || " "}
            </MenuItem>
          )}
          fullWidth={true}
          sx={{
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            backgroundColor: backgroundColor,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
          }}
          slots={{ popper: AutoWidthPopper }}
          slotProps={{
            listbox: {
              style: {
                maxHeight: 250,
                overflow: "auto",
              },
            },
            paper: {
              sx: {
                width: "max-content",
                minWidth: "100%",
              },
            },
          }}
          disabled={isReadOnly}
        />
      </div>
    );
  }
);

export default SnComboBox;
